import BaseMapController from "./base_map_controller"
import { BaseMarker, MapBounds } from "@/utils/map_interfaces"
import { Turbo } from "@hotwired/turbo-rails"

interface EncampmentMarker extends BaseMarker {
  info: {
    location: string;
    status: string;
    updated_at: string;
    image_url: string;
  };
}

export default class extends BaseMapController {
  static values = {
    ...BaseMapController.values,
    categories: { type: Object, default: {} }
  }

  declare readonly categoriesValue: Record<string, [string, string]>

  async connect() {
    await super.connect()
    await this.fetchMarkersForCurrentBounds()
  }

  protected async fetchMarkers(bounds: MapBounds): Promise<void> {
    let url;
    if (bounds) {
      url = this.addFiltersToUrl(new URL(this.markersUrlValue, window.location.origin), bounds)
    } else {
      url = new URL(this.markersUrlValue, window.location.origin)
    }

    const response = await fetch(url.toString())
    if (!response.ok) return

    const markers: EncampmentMarker[] = await response.json()
    this.clearMarkers()
    this.renderMarkers(markers)
  }

  protected addFiltersToUrl(url: URL, bounds: MapBounds = this.getBounds()) {
    if (bounds) {
      Object.entries(bounds).forEach(([key, value]) => {
        url.searchParams.set(key, value.toString())
      })
    }

    url.searchParams.set('format', 'json')

    // Preserve timeframe filter
    const currentParams = new URLSearchParams(window.location.search)
    const timeframe = currentParams.get('timeframe')
    if (timeframe) url.searchParams.set('timeframe', timeframe)

    return url
  }

  protected createMarker<T extends BaseMarker>(markerData: T): google.maps.Marker {
    const encampmentData = markerData as T & EncampmentMarker
    return new google.maps.Marker({
      position: { lat: encampmentData.latitude, lng: encampmentData.longitude },
      map: this.map!,
      title: encampmentData.info.location,
      icon: {
        url: '/marker-pin.png',
        scaledSize: new google.maps.Size(30, 40),
        anchor: new google.maps.Point(15, 40) // Center bottom of the pin
      }
    })
  }

  protected handleMarkerClick<T extends BaseMarker>(markerData: T): () => void {
    const encampmentData = markerData as T & EncampmentMarker
    const statusColor = encampmentData.info.status === 'Occupied' ? '#d1fae5' : '#F3F4F6'
    const statusTextColor = encampmentData.info.status === 'Occupied' ? '#065f46' : '#374151'
    return () => {
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="width: 250px; height: 250px; justify-items:center; padding-right: 12px;">
            <div style="width: 100%; height: 200px; margin-bottom: 0.75rem; border-radius: 0.5rem; overflow: hidden; position: relative;">
                <span style="background-color: ${statusColor}; color: ${statusTextColor}; padding: 0.25rem 0.75rem; border-radius: 0.5rem; font-size: 0.75rem; position: absolute; top: 0.5rem; left: 0.5rem;">
                  ${encampmentData.info.status}
                </span>
              <img src="${encampmentData.info.image_url}" alt="Location Image" style="width: 100%; height: 100%; object-fit: cover;">
            </div>
            <a href="/encampments/${encampmentData.id}"
               style="margin-top: 10px; width: 100%; text-align: center; padding: 0.5rem 1rem; margin-top: 0.75rem; background: #1E293B; border-radius: 0.375rem; color: #FFFFFF; text-decoration: none; font-size: 0.875rem; cursor: pointer; margin:auto;">
              View Details
            </a>
          </div>
        `
      })

      if (this.map) {
        infoWindow.open({
          map: this.map,
          anchor: this.markers.find(
            m => m.getPosition()?.lat() === encampmentData.latitude && 
                 m.getPosition()?.lng() === encampmentData.longitude
          )
        })
      }
    }
  }
  
  updateTimeframe(event: Event) {
    const select = event.target as HTMLSelectElement
    this.updateUrlHistory('timeframe', select.value)
    this.fetchMarkersForCurrentBounds()
  }

  protected updateUrlHistory(param: 'timeframe', value: string | null) {
    const currentUrl = new URL(window.location.href)
    if (value) {
      currentUrl.searchParams.set(param, value)
    } else {
      currentUrl.searchParams.delete(param)
    }
    window.history.replaceState({}, '', currentUrl.toString())
  }

  fetchMarkersForCurrentBounds(): Promise<void> {
    const bounds = this.getBounds()
    return this.fetchMarkers(bounds)
  }
} 